import React from 'react';

const CHUNK_LOAD_ERROR_PATTERN = /Loading chunk [\d]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';

  return CHUNK_LOAD_ERROR_PATTERN.test(message) || name === 'ChunkLoadError';
};

const wait = (ms: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, ms);
});

/**
 * Retry dynamic imports so transient network failures and stale chunk caches
 * do not permanently break lazy-loaded routes until a full reload.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        if (attempt >= retries) {
          break;
        }
        await wait(500 * (attempt + 1));
      }
    }

    throw lastError;
  });
}
